import React, { useEffect, useMemo, useRef, useState } from "react";
import { Globe, ChevronDown, Check, Search } from "lucide-react";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * Language picker — translates the entire page using Google Translate's
 * publicly-available website widget. No API key needed.
 *
 * - Default: English (en) or the visitor's browser language if supported.
 * - Supports 60+ languages worldwide (all major Google-Translate locales).
 * - Persists the user's choice in localStorage so the page comes back
 *   pre-translated on the next visit.
 * - Live search to quickly jump to a language across 60+ entries.
 */
const LANGUAGES = [
    // English & European
    { code: "en", label: "English", region: "World" },
    { code: "es", label: "Español (Spanish)", region: "World" },
    { code: "fr", label: "Français (French)", region: "World" },
    { code: "de", label: "Deutsch (German)", region: "Europe" },
    { code: "it", label: "Italiano (Italian)", region: "Europe" },
    { code: "pt", label: "Português (Portuguese)", region: "World" },
    { code: "nl", label: "Nederlands (Dutch)", region: "Europe" },
    { code: "sv", label: "Svenska (Swedish)", region: "Europe" },
    { code: "no", label: "Norsk (Norwegian)", region: "Europe" },
    { code: "da", label: "Dansk (Danish)", region: "Europe" },
    { code: "fi", label: "Suomi (Finnish)", region: "Europe" },
    { code: "pl", label: "Polski (Polish)", region: "Europe" },
    { code: "cs", label: "Čeština (Czech)", region: "Europe" },
    { code: "sk", label: "Slovenčina (Slovak)", region: "Europe" },
    { code: "hu", label: "Magyar (Hungarian)", region: "Europe" },
    { code: "ro", label: "Română (Romanian)", region: "Europe" },
    { code: "el", label: "Ελληνικά (Greek)", region: "Europe" },
    { code: "bg", label: "Български (Bulgarian)", region: "Europe" },
    { code: "uk", label: "Українська (Ukrainian)", region: "Europe" },
    { code: "ru", label: "Русский (Russian)", region: "Europe" },
    { code: "sr", label: "Српски (Serbian)", region: "Europe" },
    { code: "hr", label: "Hrvatski (Croatian)", region: "Europe" },
    { code: "sl", label: "Slovenščina (Slovenian)", region: "Europe" },
    { code: "lt", label: "Lietuvių (Lithuanian)", region: "Europe" },
    { code: "lv", label: "Latviešu (Latvian)", region: "Europe" },
    { code: "et", label: "Eesti (Estonian)", region: "Europe" },
    { code: "ca", label: "Català (Catalan)", region: "Europe" },
    { code: "tr", label: "Türkçe (Turkish)", region: "Europe" },

    // Middle East & Africa
    { code: "ar", label: "العربية (Arabic)", region: "Middle East / Africa" },
    { code: "he", label: "עברית (Hebrew)", region: "Middle East / Africa" },
    { code: "fa", label: "فارسی (Persian / Farsi)", region: "Middle East / Africa" },
    { code: "ps", label: "پښتو (Pashto)", region: "Middle East / Africa" },
    { code: "sw", label: "Kiswahili (Swahili)", region: "Middle East / Africa" },
    { code: "am", label: "አማርኛ (Amharic)", region: "Middle East / Africa" },
    { code: "ha", label: "Hausa", region: "Middle East / Africa" },
    { code: "yo", label: "Yorùbá", region: "Middle East / Africa" },
    { code: "zu", label: "isiZulu", region: "Middle East / Africa" },
    { code: "af", label: "Afrikaans", region: "Middle East / Africa" },

    // East & South-East Asia
    { code: "zh-CN", label: "中文 (Chinese Simplified)", region: "East Asia" },
    { code: "zh-TW", label: "中文 (Chinese Traditional)", region: "East Asia" },
    { code: "ja", label: "日本語 (Japanese)", region: "East Asia" },
    { code: "ko", label: "한국어 (Korean)", region: "East Asia" },
    { code: "vi", label: "Tiếng Việt (Vietnamese)", region: "Southeast Asia" },
    { code: "th", label: "ไทย (Thai)", region: "Southeast Asia" },
    { code: "id", label: "Bahasa Indonesia", region: "Southeast Asia" },
    { code: "ms", label: "Bahasa Melayu (Malay)", region: "Southeast Asia" },
    { code: "tl", label: "Filipino (Tagalog)", region: "Southeast Asia" },
    { code: "my", label: "မြန်မာ (Burmese)", region: "Southeast Asia" },
    { code: "km", label: "ខ្មែរ (Khmer)", region: "Southeast Asia" },
    { code: "lo", label: "ລາວ (Lao)", region: "Southeast Asia" },
    { code: "mn", label: "Монгол (Mongolian)", region: "East Asia" },

    // South Asia (India + neighbours)
    { code: "hi", label: "हिन्दी (Hindi)", region: "South Asia" },
    { code: "bn", label: "বাংলা (Bengali)", region: "South Asia" },
    { code: "mr", label: "मराठी (Marathi)", region: "South Asia" },
    { code: "te", label: "తెలుగు (Telugu)", region: "South Asia" },
    { code: "ta", label: "தமிழ் (Tamil)", region: "South Asia" },
    { code: "gu", label: "ગુજરાતી (Gujarati)", region: "South Asia" },
    { code: "kn", label: "ಕನ್ನಡ (Kannada)", region: "South Asia" },
    { code: "ml", label: "മലയാളം (Malayalam)", region: "South Asia" },
    { code: "pa", label: "ਪੰਜਾਬੀ (Punjabi)", region: "South Asia" },
    { code: "or", label: "ଓଡ଼ିଆ (Odia)", region: "South Asia" },
    { code: "ur", label: "اردو (Urdu)", region: "South Asia" },
    { code: "as", label: "অসমীয়া (Assamese)", region: "South Asia" },
    { code: "ne", label: "नेपाली (Nepali)", region: "South Asia" },
    { code: "si", label: "සිංහල (Sinhala)", region: "South Asia" },
];

const STORAGE_KEY = "aduku_lang";

/**
 * Pick a sensible first-visit default from the browser's language list.
 * Falls back to English if no match in our supported list.
 */
function detectBrowserLanguage() {
    if (typeof navigator === "undefined") return "en";
    const supported = new Set(LANGUAGES.map((l) => l.code));
    const candidates = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
    for (const c of candidates) {
        // Exact match first (e.g. "zh-CN", "en-GB")
        if (supported.has(c)) return c;
        // Strip region tag and retry ("fr-CA" → "fr")
        const base = c.split("-")[0];
        if (supported.has(base)) return base;
        // Chinese special-cases — "zh" alone → simplified
        if (base === "zh") return "zh-CN";
    }
    return "en";
}

function setGoogleCookie(lang) {
    // Google Translate reads the lang preference from a cookie named
    // `googtrans` whose value is `/<source>/<target>`. Set on both the
    // current host and the parent domain so it survives sub-domain navigation.
    const value = lang === "en" ? "" : `/en/${lang}`;
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
    const host = window.location.hostname;
    document.cookie = `googtrans=${value}; expires=${expires}; path=/`;
    if (host.includes(".")) {
        const parent = "." + host.split(".").slice(-2).join(".");
        document.cookie = `googtrans=${value}; expires=${expires}; path=/; domain=${parent}`;
    }
}

export default function LanguagePicker() {
    const [current, setCurrent] = useState("en");
    const [q, setQ] = useState("");
    const inited = useRef(false);

    useEffect(() => {
        if (inited.current) return;
        inited.current = true;

        let saved;
        try {
            saved = localStorage.getItem(STORAGE_KEY);
            const userPicked = localStorage.getItem("aduku_lang_user_picked") === "1";
            // v79.192 — Founder direction (Feb 2026): "set default
            // language English." Reset the stored language to `en`
            // on every visit UNLESS the user has explicitly picked a
            // different language via the picker (we track that with
            // the `aduku_lang_user_picked` flag written in `change`
            // below). Prior behaviour let stale localStorage values
            // survive across releases even after the founder wanted a
            // fresh English default.
            if (!saved || !userPicked) {
                saved = "en";
                localStorage.setItem(STORAGE_KEY, saved);
            }
        } catch {
            saved = "en";
        }
        setCurrent(saved);

        // 1) Set the googtrans cookie BEFORE the script loads so the page
        //    initialises in the chosen language.
        setGoogleCookie(saved);

        // 2) Inject Google's translate script & init function (idempotent).
        if (!document.getElementById("google-translate-script")) {
            window.googleTranslateElementInit = () => {
                /* global google */
                new window.google.translate.TranslateElement(
                    { pageLanguage: "en", autoDisplay: false, includedLanguages: LANGUAGES.map((l) => l.code).join(",") },
                    "google_translate_element",
                );
            };
            const s = document.createElement("script");
            s.id = "google-translate-script";
            s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
            s.async = true;
            document.body.appendChild(s);
        }
    }, []);

    const change = (code) => {
        try {
            localStorage.setItem(STORAGE_KEY, code);
            // v79.192 — Mark that the user has explicitly chosen a
            // language so we don't overwrite their pick with English
            // on the next visit.
            localStorage.setItem("aduku_lang_user_picked", "1");
        } catch { /* noop */ }
        setCurrent(code);
        setGoogleCookie(code);
        // Reload so Google's script picks up the new googtrans cookie cleanly.
        window.location.reload();
    };

    const cur = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

    // Filter + group by region for the dropdown body.
    const grouped = useMemo(() => {
        const needle = q.trim().toLowerCase();
        const filtered = needle
            ? LANGUAGES.filter((l) => l.label.toLowerCase().includes(needle) || l.code.toLowerCase().includes(needle))
            : LANGUAGES;
        const order = ["World", "Europe", "Middle East / Africa", "East Asia", "Southeast Asia", "South Asia"];
        const buckets = {};
        for (const l of filtered) (buckets[l.region] ||= []).push(l);
        return order.filter((r) => buckets[r]?.length).map((r) => [r, buckets[r]]);
    }, [q]);

    return (
        <>
            {/* Hidden host element required by Google's script. */}
            <div id="google_translate_element" style={{ display: "none" }} />
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <button
                        data-testid="language-picker-trigger"
                        translate="no"
                        aria-label={`Language — ${cur.label}. Tap to translate to your language.`}
                        title="Translate — pick your language"
                        className="notranslate relative h-10 w-10 sm:w-auto sm:px-3.5 rounded-full bg-aduku-yellow text-aduku-navy text-xs sm:text-sm font-black flex items-center justify-center sm:justify-start gap-1 sm:gap-1.5 transition-transform shrink-0 hover:brightness-110 active:scale-95 shadow-[0_0_0_0_rgba(255,199,0,0.7)] animate-[adukoo-lang-glow_2.4s_ease-in-out_infinite]"
                    >
                        <Globe className="w-4 h-4" />
                        <span className="hidden sm:inline">{cur.label.split(" ")[0]}</span>
                        <ChevronDown className="w-3 h-3 opacity-70 hidden sm:inline" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    className="w-72 max-h-[70vh] overflow-hidden flex flex-col notranslate p-0"
                    translate="no"
                >
                    <div className="p-2 border-b border-aduku-border sticky top-0 bg-white z-10">
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 text-aduku-navy/50 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <input
                                data-testid="language-picker-search"
                                autoFocus
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search 60+ languages"
                                className="w-full h-9 pl-7 pr-2 rounded-md border border-aduku-border bg-aduku-bg text-sm focus:outline-none focus:border-aduku-navy"
                            />
                        </div>
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {grouped.length === 0 && (
                            <div className="px-3 py-6 text-center text-sm text-aduku-navy/55">
                                No language matches “{q}”.
                            </div>
                        )}
                        {grouped.map(([region, langs]) => (
                            <div key={region}>
                                <div className="px-3 pt-2.5 pb-1 text-[10px] uppercase tracking-wider font-bold text-aduku-navy/55">{region}</div>
                                {langs.map((l) => (
                                    <button
                                        key={l.code}
                                        type="button"
                                        data-testid={`language-option-${l.code}`}
                                        onClick={() => change(l.code)}
                                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-aduku-yellow/15 ${
                                            current === l.code ? "bg-aduku-yellow/20 font-semibold" : ""
                                        }`}
                                    >
                                        <span className="text-aduku-navy">{l.label}</span>
                                        {current === l.code && <Check className="w-3.5 h-3.5 text-aduku-yellow-600 shrink-0 ml-2" />}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
}
